import type { LayerAnimation, LayerSchedule } from '@flobi/artwork-core';
import { phaseValues, withPhase } from './animateModel';

/**
 * The timeline, as numbers: which rows, where each bar sits, and what a drag of a bar's
 * end, of a layer's arriving or of the page's end is allowed to write. Pure, so the geometry is tested without a pointer.
 *
 * When a layer arrives, loops and leaves is NOT worked out here. It used to be, from the
 * animation, and a title arriving letter by letter was drawn as one short bar with its
 * loop starting seconds early. The engine's `stageSchedule` answers instead: the numbers
 * playback itself uses.
 */

/** One shown direct child of the page, as the canvas hands it over: the engine's schedule for it, and its name. */
export interface StripInput extends LayerSchedule {
  readonly name: string;
  readonly animation?: LayerAnimation | null;
}

/** A span of seconds. */
export interface Span {
  readonly from: number;
  readonly until: number;
}

export interface StripRow {
  readonly id: string;
  readonly name: string;
  /** When it is on the page: the bar. */
  readonly window: Span;
  /** Whether that window was set, rather than the whole page by default. */
  readonly timed: boolean;
  /** The arriving part of the bar, or null when it is simply there. */
  readonly enter: Span | null;
  /** The leaving part, which finishes as the window ends. */
  readonly exit: Span | null;
  /** Whether it loops while it is on the page. */
  readonly loops: boolean;
  /** Whether its motion is its own rather than the page's. */
  readonly own: boolean;
  readonly animation: LayerAnimation | null;
  /** Full entrance, including staggered text, before display clipping. */
  readonly enterDuration: number;
}

const clampSpan = (span: Span, within: Span): Span | null => {
  const from = Math.max(within.from, Math.min(within.until, span.from));
  const until = Math.max(from, Math.min(within.until, span.until));
  return until > from ? { from, until } : null;
};

/**
 * The rows, top of the stack first — the order the Layers panel reads, so the two agree.
 * Each part is held inside the layer's own window: an arrival that would run past it is
 * drawn as far as the window goes.
 */
export function stripRows(input: { children: readonly StripInput[] }): StripRow[] {
  return input.children
    .map((child): StripRow => ({
      id: child.id,
      name: child.name,
      window: child.window,
      timed: child.timed,
      enter: child.enter ? clampSpan(child.enter, child.window) : null,
      exit: child.exit ? clampSpan(child.exit, child.window) : null,
      loops: child.loops,
      own: child.own,
      animation: child.animation ?? null,
      enterDuration: child.enter ? child.enter.until - child.enter.from : 0,
    }))
    .reverse();
}

// ── geometry ─────────────────────────────────────────────────────────────────

/** Seconds to a distance along a track `width` wide showing a page that plays for `duration`. */
export function timeToX(seconds: number, duration: number, width: number): number {
  if (duration <= 0) return 0;
  return (Math.max(0, Math.min(duration, seconds)) / duration) * width;
}

/** And back, held inside the page. */
export function xToTime(x: number, duration: number, width: number): number {
  if (width <= 0) return 0;
  return Math.max(0, Math.min(duration, (x / width) * duration));
}

/** The shortest window a drag leaves, so a layer can never be dragged off the page. */
export const SHORTEST = 0.1;
/** Where a dragged end lands: to the twentieth of a second. */
export const SNAP = 0.05;

const snap = (value: number) => Math.round(value / SNAP) * SNAP;
const tidy = (value: number) => Math.round(value * 1000) / 1000;

/**
 * A window with one end dragged to `seconds`: snapped, held inside the page, and never
 * closer to the other end than SHORTEST — `from < until` always, as the schema requires.
 */
export function dragEdge(window: Span, edge: 'from' | 'until', seconds: number, duration: number): Span {
  const at = snap(seconds);
  if (edge === 'from') {
    const from = Math.max(0, Math.min(at, window.until - SHORTEST));
    return { from: tidy(from), until: tidy(window.until) };
  }
  const until = Math.min(duration, Math.max(at, window.from + SHORTEST));
  return { from: tidy(window.from), until: tidy(until) };
}

/**
 * How much time the ruler shows: the page, and room past its end to drag the end out into
 * — a page that fills the ruler exactly has nowhere to grow. Whole seconds, so the marks
 * stay where they were while the end is dragged a little.
 */
export function rulerSpan(length: number): number {
  return Math.min(61, Math.max(2, Math.ceil(length + 0.5) + 1));
}

/** The shortest and longest a page can be set to last. */
export const PAGE_SHORTEST = 0.5;
export const PAGE_LONGEST = 60;

/** Where a dragged page end lands: to the tenth of a second, inside what a page can last. */
export function dragLength(seconds: number): number {
  return tidy(Math.min(PAGE_LONGEST, Math.max(PAGE_SHORTEST, Math.round(seconds * 10) / 10)));
}

/**
 * A layer's wait before it arrives, after its arriving is dragged `by` seconds along the
 * timeline: snapped, never before its window opens and never so late that the arriving
 * would run past the window's end.
 */
export function dragDelay(row: { window: Span; enter: Span }, by: number): number {
  const room = Math.max(0, row.window.until - row.window.from - (row.enter.until - row.enter.from));
  return tidy(Math.max(0, Math.min(room, snap(row.enter.from - row.window.from + by))));
}

export type MotionPhase = 'in' | 'out';
export interface MotionResize {
  readonly range: Span;
  readonly span: number;
  readonly delay?: number;
  readonly textStagger?: number;
  readonly window?: Span;
}

/** Resize the purple motion, independently of the layer's visibility window.
 * Text's per-unit duration and stagger scale together so the displayed end stays honest.
 * An exit stays attached to the layer's end; dragging its right edge also trims that end.
 */
export function resizeMotion(row: StripRow, phase: MotionPhase, edge: 'from' | 'until', seconds: number, length: number): MotionResize | null {
  const range = phase === 'in' ? row.enter : row.exit;
  const animation = row.animation;
  if (!range || !animation) return null;
  const unitSpan = phaseValues(animation, phase).span;
  const full = phase === 'in' ? row.enterDuration : range.until - range.from;
  const stagger = phase === 'in' ? animation.textStagger : 0;
  const gaps = stagger > 0 ? Math.max(0, (full - unitSpan) / stagger) : 0;
  const anchor = edge === 'from' ? range.until : range.from;
  const room = edge === 'from' ? anchor - row.window.from : (phase === 'out' ? length : row.window.until) - anchor;
  const duration = tidy(Math.max(0.05, Math.min(room, 10 + gaps, snap(edge === 'from' ? anchor - seconds : seconds - anchor))));
  const nextRange = edge === 'from' ? { from: tidy(anchor - duration), until: anchor } : { from: anchor, until: tidy(anchor + duration) };
  const span = phase === 'in' ? Math.max(0.05, Math.min(10, unitSpan * duration / Math.max(0.05, full))) : duration;
  return {
    range: nextRange,
    span,
    ...(phase === 'in' ? { delay: tidy(Math.max(0, nextRange.from - row.window.from)) } : {}),
    ...(gaps > 0 ? { textStagger: Math.max(0, Math.min(1, (duration - span) / gaps)) } : {}),
    ...(phase === 'out' && edge === 'until' ? { window: { ...row.window, until: nextRange.until } } : {}),
  };
}

/** Keep the other phases and every unrelated motion setting when committing a resize. */
export function applyMotionResize(animation: LayerAnimation, phase: MotionPhase, resize: MotionResize): LayerAnimation {
  return {
    ...withPhase(animation, phase, { span: resize.span }),
    ...(resize.delay !== undefined ? { delay: resize.delay } : {}),
    ...(resize.textStagger !== undefined ? { textStagger: resize.textStagger } : {}),
  };
}

/** The ruler's marks: whole seconds, thinned so they never crowd. */
export function rulerMarks(duration: number, width: number, gap = 36): number[] {
  if (duration <= 0 || width <= 0) return [0];
  const steps = [0.5, 1, 2, 5, 10, 15, 30];
  const step = steps.find((one) => (one / duration) * width >= gap) ?? 60;
  const marks: number[] = [];
  for (let t = 0; t <= duration + 1e-9; t += step) marks.push(tidy(t));
  return marks;
}
